import apiClient from '../api/client';

export const contentTypeFromHeader = (value: unknown): string => (
  typeof value === 'string' && value.trim()
    ? value
    : 'application/octet-stream'
);

const filenameFromDisposition = (disposition: string | undefined, fallback: string): string => {
  if (!disposition) return fallback;

  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try { return decodeURIComponent(encoded).replace(/^.*[\\/]/, ''); }
    catch { /* Fall through to the ordinary filename form. */ }
  }

  const ordinary = disposition.match(/filename="?([^";\n]+)"?/i)?.[1]?.trim();
  return ordinary ? ordinary.replace(/^.*[\\/]/, '') : fallback;
};

/** Download a protected API resource without navigating away from the app. */
export const downloadAuthenticatedFile = async (endpoint: string, fallbackFilename: string): Promise<string> => {
  const response = await apiClient.get(endpoint, { responseType: 'blob' });
  const dispositionHeader = response.headers['content-disposition'];
  const contentTypeHeader = response.headers['content-type'];
  const filename = filenameFromDisposition(
    typeof dispositionHeader === 'string' ? dispositionHeader : undefined,
    fallbackFilename,
  );
  const contentType = contentTypeFromHeader(contentTypeHeader);
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: contentType });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  try {
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }

  return filename;
};

export { filenameFromDisposition };
